import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRightIcon } from 'lucide-react';
type Role = {
  id: string;
  org: string;
  title: string;
  period: string;
  location: string;
  highlights: string[];
};
const ROLES: Role[] = [{
  id: 'boi',
  org: 'Bank of Ireland',
  title: 'US Head of Finance',
  period: '12/2022 – Present',
  location: 'New York',
  highlights: ['Acts as primary financial advisor to Executive Leadership and the Board on regional strategy and fiscal governance.', 'Delivered $4.3M in recurring savings through real estate, insurance, and organizational optimization.', 'Spearheaded SAP S/4HANA rollout and customized ERP workflows to meet US regulatory mandates (FFIEC, FR, TICS).']
}, {
  id: 'mufg',
  org: 'MUFG',
  title: 'Advisor, Regulatory Reporting',
  period: '2021 – 2022',
  location: 'New York',
  highlights: ['Architected enterprise reporting frameworks for OCC/FRB compliance.', 'Automated reporting workflows saving 570 FTE hours annually.']
}, {
  id: 'jpm',
  org: 'Mizuho / JPMorgan Chase',
  title: 'Executive Director & Controller',
  period: '2017 – 2019',
  location: 'New York',
  highlights: ['Designed the Regulation W compliance framework across affiliate transactions.', 'Collaborated with Global Treasury to release $24B in regulatory capital.']
}, {
  id: 'db',
  org: 'Deutsche Bank',
  title: 'SVP, Structured Reporting & Regulation W',
  period: '2014 – 2017',
  location: 'New York',
  highlights: ['Successfully resolved 88 critical regulatory findings (MRAs/MIRAs).', 'Remediation spanned FR Y-9C, FFIEC 031, and FR Y-7 reporting suites.']
}, {
  id: 'earlier',
  org: 'RBS · CIT · Barclays · Merrill Lynch',
  title: 'Senior Finance Leadership Roles',
  period: 'Earlier Career',
  location: 'New York · London',
  highlights: ['Managed cross-border teams of up to 15 direct reports.', 'Led post-merger integration work including the Lehman Brothers acquisition.', 'Delivered SIFI regulatory transformation programs across multiple jurisdictions.']
}];
export function Experience() {
  const [activeId, setActiveId] = useState(ROLES[0].id);
  const active = ROLES.find(r => r.id === activeId) ?? ROLES[0];
  return <section aria-labelledby="experience-heading" className="w-full bg-navy-950" data-id="element-100" data-mp="2hna61k8v2cba">
      <div className="mx-auto max-w-content px-6 py-20 sm:py-24 lg:px-8" data-id="element-101" data-mp="2cnnsf9p4eu1y">
        <h2 id="experience-heading" className="text-[11px] font-semibold uppercase tracking-widest text-gold-400" data-id="element-102" data-mp="27okra0c9h7z9">
          Role-by-Role Mandates
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slateink-300" data-id="element-103" data-mp="w9pdshpcpcka">
          Select an institution to review the mandate held and the specific deliverables
          completed in that role.
        </p>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-104" data-mp="33vz6yvoq2kh3">
          <div className="lg:col-span-5" data-id="element-105" data-mp="37x0yeqceds8i">
            <ul role="tablist" aria-label="Roles" className="border-l border-white/15" data-id="element-106" data-mp="37xu7hxytwttt">
              {ROLES.map(role => {
              const isActive = role.id === active.id;
              return <li key={role.id} className="relative" data-id="element-107" data-mp="3hof3ddn4bk91">
                    <button type="button" role="tab" id={`tab-${role.id}`} aria-selected={isActive} aria-controls={`panel-${role.id}`} onClick={() => setActiveId(role.id)} className="group flex w-full items-start gap-4 py-5 pl-6 pr-3 text-left transition-colors" data-id="element-108" data-mp="3mmgk3rx2n7hc">
                      <span aria-hidden="true" className={['absolute left-0 top-0 h-full w-[2px] transition-colors duration-300', isActive ? 'bg-gold-500' : 'bg-transparent group-hover:bg-white/25'].join(' ')} data-id="element-109" data-mp="21a5dgf389ng3" />
                      <span className="flex-1" data-id="element-110" data-mp="1kw80fs82yyy0">
                        <span className="block text-[11px] uppercase tracking-widest text-slateink-500" data-id="element-111" data-mp="1kuwxtvqdfx8h">
                          {role.period}
                        </span>
                        <span className={['mt-2 block font-serif text-lg transition-colors', isActive ? 'text-white' : 'text-slateink-300 group-hover:text-white'].join(' ')} data-id="element-112" data-mp="s7sojek8wwqw">
                          {role.org}
                        </span>
                        <span className="mt-1 block text-sm text-slateink-500" data-id="element-113" data-mp="9fq55vg7z2cy">
                          {role.title}
                        </span>
                      </span>
                      <ChevronRightIcon aria-hidden="true" className={['mt-6 h-4 w-4 flex-none transition-all duration-300', isActive ? 'translate-x-0 text-gold-500 opacity-100' : '-translate-x-1 text-slateink-500 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'].join(' ')} data-id="element-114" data-mp="2g51vwd81eb0k" />
                    </button>
                  </li>;
            })}
            </ul>
          </div>

          <div className="lg:col-span-7" data-id="element-115" data-mp="2pv4mk6rorcvq">
            <AnimatePresence mode="wait" data-id="element-116" data-mp="2pu1v9xqgb8fa">
              <motion.div key={active.id} role="tabpanel" id={`panel-${active.id}`} aria-labelledby={`tab-${active.id}`} initial={{
              opacity: 0,
              y: 14
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.35,
              ease: [0.22, 1, 0.36, 1]
            }} className="border border-white/10 bg-navy-900/70 p-8 sm:p-10" data-id="element-117" data-mp="2tw6r934dgh53">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-118" data-mp="3bdvomqglz8zf">
                  <span className="text-[11px] uppercase tracking-widest text-gold-400" data-id="element-119" data-mp="3bbsohdlqrzgz">
                    {active.period}
                  </span>
                  <span className="h-px w-6 bg-white/20" aria-hidden="true" data-id="element-120" data-mp="32dh1rmnzxltt" />
                  <span className="text-[11px] uppercase tracking-widest text-slateink-500" data-id="element-121" data-mp="2xedurwogh5j7">
                    {active.location}
                  </span>
                </div>

                <h3 className="mt-6 font-serif text-2xl text-white sm:text-3xl" data-id="element-122" data-mp="2sguiv3f8d4uf">
                  {active.org}
                </h3>
                <p className="mt-2 text-sm font-medium uppercase tracking-wider text-slateink-300" data-id="element-123" data-mp="qo6bchvjzv8u">
                  {active.title}
                </p>

                <ul className="mt-8 space-y-5" data-id="element-124" data-mp="lmgycwort7i4">
                  {active.highlights.map(point => <li key={point} className="flex gap-4" data-id="element-125" data-mp="u3hj9tr9i71o">
                      <span className="mt-2 h-1.5 w-1.5 flex-none bg-gold-500" aria-hidden="true" data-id="element-126" data-mp="y5urcotheh1m" />
                      <span className="text-[15px] leading-relaxed text-slateink-100" data-id="element-127" data-mp="17xic66vj8zu4">
                        {point}
                      </span>
                    </li>)}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}